"use client";
import { Image } from "@/components/shared/image";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useEffect, useMemo, useState } from "react";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Lang } from "@/i18n/settings";
import { useWindowSize } from "@/hooks/use-window-size";
import { LeftArrow, RightArrow } from "./CarouselArrows";

type CoverImageProps = {
  lang: Lang;
};

const desktopImages = [
  "/assets/heroes/hero-2024.jpg",
  "/assets/heroes/hero-2025.jpg",
  "/assets/heroes/hero-2026.jpg",
];
const mobileImages = [
  "/assets/heroes/hero-2024-mobile.jpg",
  "/assets/heroes/hero-2025-mobile.jpg",
  "/assets/heroes/hero-2026-mobile.jpg",
];

export default function CoverImage(props: CoverImageProps) {
  const { lang } = props;
  const isRtl = lang === "ar";

  const { width } = useWindowSize();

  const isMd = width < 768;

  const images = useMemo(() => {
    if (!width) return desktopImages;
    return isMd ? mobileImages : desktopImages;
  }, [width]);

  const [emblaApi, setEmblaApi] = useState<CarouselApi>();
  const [isInitialized, setIsInitialized] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    setIsInitialized(true);
    const updateScrollButtons = () => {
      setCanScrollNext(emblaApi.canScrollNext());
      setCanScrollPrev(emblaApi.canScrollPrev());
    };

    emblaApi.on("select", updateScrollButtons);

    updateScrollButtons();

    return () => {
      emblaApi.off("select", updateScrollButtons);
    };
  }, [emblaApi]);

  const handleNext = () => {
    if (isRtl) {
      emblaApi?.scrollPrev();
    } else {
      emblaApi?.scrollNext();
    }
  };

  const handlePrev = () => {
    if (isRtl) {
      emblaApi?.scrollNext();
    } else {
      emblaApi?.scrollPrev();
    }
  };

  return (
    <AspectRatio ratio={isMd ? 640 / 491 : 32 / 19} className="!pb-0">
      <Carousel
        opts={{
          direction: isRtl ? "rtl" : "ltr",
          startIndex: images.length - 1,
        }}
        setApi={setEmblaApi}
        className="pointer-events-none"
      >
        <CarouselContent className={`${isInitialized ? "visible" : "invisible"} `}>
          {images.map((image, index) => {
            return (
              <CarouselItem className="" key={index}>
                <div className="image-wrapper">
                  <Image
                    src={image}
                    width={5760}
                    height={3420}
                    alt="Hero"
                    className="object-cover"
                  />
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>

      <div className="container relative z-40 px-3 xs:px-[30px] sm:px-4 3xl:max-w-[105rem] 3xl:px-0">
        <div className="absolute bottom-4 left-4 z-10">
          <LeftArrow
            onClick={handlePrev}
            canScrollNext={canScrollNext}
            canScrollPrev={canScrollPrev}
            isRtl={isRtl}
          />
        </div>

        <div className="absolute bottom-4 right-4 z-10">
          <RightArrow
            onClick={handleNext}
            canScrollNext={canScrollNext}
            canScrollPrev={canScrollPrev}
            isRtl={isRtl}
          />
        </div>
      </div>
    </AspectRatio>
  );
}
